import { FolderArrowDownIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";

import { AI_BACKEND_URL_DOWNLOAD_HARMONY_MAKER_FILES } from "@/constants/constants";
import { DOWNLOAD_AUDIO_TYPES } from "@/constants/posthog/downloadAudio";
import { trackAudioDownload } from "@/data/posthog/trackAudioDownload";
import {
  ConversionConstructedNoDates,
  MixedModelConstructedNoDates,
  ModelNoDates,
  SoundNoDates,
} from "@/types/types";
import { getArtistInstrumentModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getArtistInstrumentModelMixedModel";
import { cn } from "@/utils/cn";
import { createMixedModelName } from "@/utils/createMixedModelName";

export function DownloadConversionMulti({
  conversionMulti,
  models,
  mixedModelsConstructed,
  className,
}: {
  conversionMulti: ConversionConstructedNoDates[];
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
  className?: string;
}) {
  // Should never happen
  if (conversionMulti.length === 0) {
    return null;
  }

  const firstConversion = conversionMulti[0];

  const artistInstrumentModelMixedModelFirstConversion =
    getArtistInstrumentModelMixedModel(
      firstConversion.voiceId,
      models,
      mixedModelsConstructed
    );

  // Construct zip file name
  let zipFileName = `${firstConversion.name}`;

  let nameArtistInstrumentModelMixedModelFirstConversion: string;
  if (artistInstrumentModelMixedModelFirstConversion === null) {
    nameArtistInstrumentModelMixedModelFirstConversion = "audimee";
  } else {
    nameArtistInstrumentModelMixedModelFirstConversion =
      "name" in artistInstrumentModelMixedModelFirstConversion ?
        artistInstrumentModelMixedModelFirstConversion.name
      : createMixedModelName(artistInstrumentModelMixedModelFirstConversion);
  }

  zipFileName += `_${nameArtistInstrumentModelMixedModelFirstConversion}`;

  // Track used filenames to prevent duplicates
  const usedFilenames = new Set<string>();

  // Construct download instructions for each file
  const downloadInstructions = conversionMulti.map((conversion) => {
    let sound: SoundNoDates | null;
    let extension: "wav" | "mp3" | null;
    if (conversion.outputSoundWav !== null) {
      sound = conversion.outputSoundWav;
      extension = "wav";
    } else if (conversion.outputSoundMp3 !== null) {
      sound = conversion.outputSoundMp3;
      extension = "mp3";
    } else {
      sound = null;
      extension = null;
    }

    if (sound === null || extension === null) {
      return;
    }

    // Construct the file name
    const artistInstrumentModelMixedModel = getArtistInstrumentModelMixedModel(
      conversion.voiceId,
      models,
      mixedModelsConstructed
    );

    let conversionFilename = `${conversion.name}`;

    let nameArtistInstrumentModelMixedModel: string;
    if (artistInstrumentModelMixedModel === null) {
      nameArtistInstrumentModelMixedModel = "audimee";
    } else {
      nameArtistInstrumentModelMixedModel =
        "name" in artistInstrumentModelMixedModel ?
          artistInstrumentModelMixedModel.name
        : createMixedModelName(artistInstrumentModelMixedModel);
    }

    conversionFilename += `_${nameArtistInstrumentModelMixedModel}`;

    if (conversion.pitchShift !== null && conversion.pitchShift !== 0) {
      conversionFilename += `_`;

      if (conversion.pitchShift > 0) {
        conversionFilename += `+`;
      }

      conversionFilename += `${conversion.pitchShift}`;
    }

    // Create base filename and check for duplicates
    const baseFilename = `${conversionFilename}.${extension}`;
    let filenameInZip = baseFilename;
    let counter = 1;

    // If filename already exists, append a counter
    while (usedFilenames.has(filenameInZip)) {
      filenameInZip = `${conversionFilename}_${counter}.${extension}`;
      counter++;
    }

    // Add to set of used filenames
    usedFilenames.add(filenameInZip);

    const soundFileUrl = sound.presignedUrlDownload;
    return {
      soundFileUrl: soundFileUrl,
      filenameInZip: filenameInZip,
      lengthS: conversion.lengthS,
      pan: 0,
      timeShift: 0,
      ownerId: conversion.ownerId,
    };
  });

  const trackDownload = () => {
    trackAudioDownload({
      audioType: DOWNLOAD_AUDIO_TYPES.CONVERSION,
      entityId:
        firstConversion.conversionMultiId !== null ?
          firstConversion.conversionMultiId
        : firstConversion.id,
    });
  };

  return (
    <form
      className={cn("h-6 w-6", className)}
      action={AI_BACKEND_URL_DOWNLOAD_HARMONY_MAKER_FILES}
      method="POST"
    >
      <input
        type="hidden"
        name="data"
        value={JSON.stringify({
          downloadInstructions: downloadInstructions,
          conversionGroupfilenameWithoutExtension: zipFileName,
          doProcessing: false,
        })}
      />
      <button type="submit" className="h-6 w-6" onClick={trackDownload}>
        <FolderArrowDownIcon
          data-tooltip-id={`download-conversion-group-tooltip-${firstConversion.id}`}
          data-tooltip-content="Download"
          data-tooltip-place="top"
          className="h-6 w-6 stroke-gray-900"
        />
        <Tooltip
          id={`download-conversion-group-tooltip-${firstConversion.id}`}
        />
      </button>
    </form>
  );
}
